// Phase T2.0A Prompt 4 — Home Featured Hero.
//
// Admin-curated rotation. NEVER falls back to random/newest events.
// Renders nothing (returns null) when the backend returns an empty list.
//
// Behaviours:
//   • Flyer-first hero — image fills the top, text overlays a soft gradient.
//   • No-flyer fallback — elegant text-first card with a subtle green gradient.
//   • Multi-event rotation: auto-advances every 7s, pause on hover / focus /
//     manual interaction. Dots + prev/next arrows (visible on ≥md, always on
//     touch via swipe). Touch swipe supported.
//   • Respects prefers-reduced-motion.
//   • Deterministic order (backend already sorts).
//   • Whole card is one large tappable target → correct canonical route.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, CaretRight, Trophy, Users, MapPin, Calendar } from '@phosphor-icons/react';
import { homeFeaturedAPI } from '../../lib/homeFeaturedApi';
import { playerCapacity } from '../../lib/capacity';
import ManageEventPill from '../events/ManageEventPill';

const AUTOROTATE_MS = 7000;

const CLUB_CATEGORY_LABEL = {
  social:  'Social',
  tennis:  'Tennis',
  charity: 'Charity',
  club:    'Club',
};

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on);
    else mq.addListener?.(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else mq.removeListener?.(on);
    };
  }, []);
  return reduced;
}

function fmtDateShort(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return null; }
}

function fmtTimeShort(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return null; }
}

function feeLabel(cents) {
  if (cents == null) return null;
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)} per player`;
}

function statusPill(item) {
  // Club events don't have a registration lifecycle — no pill needed.
  if (item.kind === 'club_event') return null;
  const s = item.status;
  const cap = playerCapacity(item.event_type, item.max_entries);
  const isFull = cap != null && (item.registered_player_count || 0) >= cap;

  if (s === 'completed') return { label: 'COMPLETED', tone: 'zinc' };
  if (s === 'in_progress') return { label: 'IN PROGRESS', tone: 'blue' };
  if (s === 'registration_closed') return { label: 'REGISTRATION CLOSED', tone: 'zinc' };
  if (s === 'registration_open' && isFull) return { label: 'FULL', tone: 'amber' };
  if (s === 'registration_open') return { label: 'REGISTRATION OPEN', tone: 'emerald' };
  if (s === 'draft') return { label: 'DRAFT', tone: 'zinc' };
  if (s === 'archived') return { label: 'ARCHIVED', tone: 'zinc' };
  return { label: (s || '').replace(/_/g, ' ').toUpperCase(), tone: 'zinc' };
}

const TONE_CLS = {
  emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  amber:   'bg-amber-50 text-amber-900 border-amber-200',
  blue:    'bg-sky-50 text-sky-900 border-sky-200',
  zinc:    'bg-zinc-100 text-zinc-700 border-zinc-200',
};

function ViewerPill({ state }) {
  if (state === 'registered') {
    return (
      <span
        data-testid="featured-viewer-registered"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-500/95 text-white rounded-full px-2 py-0.5"
      >
        You&apos;re registered
      </span>
    );
  }
  if (state === 'alternate') {
    return (
      <span
        data-testid="featured-viewer-alternate"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-400 text-amber-950 rounded-full px-2 py-0.5"
      >
        Alternate
      </span>
    );
  }
  return null;
}

function CountLabel({ item }) {
  const n = item.registered_player_count || 0;
  const cap = playerCapacity(item.event_type, item.max_entries);
  if (cap == null) return `${n} Player${n === 1 ? '' : 's'} Registered`;
  return `${n} / ${cap} Players Registered`;
}

const COMPOSITION_LABEL = {
  open: '',
  mens: "Men's",
  womens: "Women's",
  mixed: 'Mixed',
};

// Map the (already content-gated) backend CTA into short, sporty copy for the
// badge. Gating is preserved — we only ever surface what the backend decided.
function shortCompletedCta(cta) {
  const p = (cta && cta.primary) || '';
  const sec = (cta && cta.secondary) || '';
  if (p.startsWith('Meet Your Champions')) {
    return { main: 'View Champions', support: sec.includes('Photos') ? 'Champions • Photos' : 'Champions' };
  }
  if (p.startsWith('The Results Are In')) {
    return { main: 'See Results', support: 'Standings' };
  }
  if (p.startsWith('Relive the Fun')) {
    return { main: 'See Highlights', support: 'Photos' };
  }
  return { main: 'View Recap', support: null };
}


function Slide({ item, onOpen, testIdIndex }) {
  const flyer = item.flyer_url;
  const pill = statusPill(item);
  const showRegisteredCount = item.max_entries != null || (item.registered_player_count || 0) > 0;
  const startsDate = fmtDateShort(item.starts_at);
  const startsTime = fmtTimeShort(item.starts_at);
  // Club events don't have "per player" semantics — render just "$25". Also,
  // suppress the "Free" chip for club events (0 alone is not payment-on).
  const fee = item.kind === 'club_event'
    ? (Number.isFinite(item.entry_fee_cents) && item.entry_fee_cents > 0
        ? (item.entry_fee_cents % 100 === 0
            ? `$${item.entry_fee_cents / 100}`
            : `$${(item.entry_fee_cents / 100).toFixed(2)}`)
        : null)
    : feeLabel(item.entry_fee_cents);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`featured-slide-${testIdIndex}`}
      data-slide-kind={item.kind}
      data-slide-event-id={item.id}
      className="group relative w-full text-left rounded-3xl overflow-hidden border border-[#013523]/15 bg-white shadow-sm hover:shadow-md transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#013523]"
      style={{ aspectRatio: '16 / 10', maxHeight: 520 }}
    >
      {/* Background — flyer or elegant text-first fallback */}
      {flyer ? (
        <img
          src={flyer}
          alt=""
          loading="eager"
          decoding="async"
          data-testid={`featured-flyer-${testIdIndex}`}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <div
          data-testid={`featured-fallback-${testIdIndex}`}
          className="absolute inset-0 bg-gradient-to-br from-[#013523] via-[#024029] to-[#015e3f]"
        >
          <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_top_right,white_0%,transparent_50%)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Trophy size={72} weight="duotone" className="text-white/25" />
          </div>
        </div>
      )}

      {/* Gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />

      {/* Completed-event CTA — compact, sporty "sticker" badge anchored
          bottom-right. Non-interactive children so clicks bubble to the card
          button and open the event detail page. Short active copy derived from
          the backend's content-gated CTA (gating unchanged). */}
      {item.completed_cta && (() => {
        const sc = shortCompletedCta(item.completed_cta);
        return (
          <div
            data-testid={`featured-completed-cta-${testIdIndex}`}
            className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 z-10 flex flex-col items-end"
          >
            <div className="flex items-stretch rounded-full overflow-hidden shadow-lg ring-1 ring-black/15 -rotate-2 origin-bottom-right transition-transform group-hover:-rotate-1 group-hover:scale-[1.04]">
              <span className="flex items-center gap-1 bg-[#c9a93a] text-[#013523] text-[8px] sm:text-[9px] font-extrabold uppercase tracking-wider pl-2 pr-1.5">
                <Trophy size={11} weight="fill" className="shrink-0" />
                <span className="hidden sm:inline">Completed</span>
              </span>
              <span className="flex items-center gap-1 bg-[#013523] text-[#f7f1e1] text-[12px] sm:text-[13px] font-extrabold pl-2.5 pr-2.5 py-1.5">
                {sc.main}
                <CaretRight size={12} weight="bold" className="text-[#c9a93a] shrink-0" />
              </span>
            </div>
            {sc.support && (
              <span className="mt-1 text-[8px] sm:text-[9px] font-bold uppercase tracking-wider text-[#f7f1e1]/90 drop-shadow">
                {sc.support}
              </span>
            )}
          </div>
        );
      })()}

      {/* Top-left: kind + category (club events) + featured tag */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap max-w-[85%]">
        <span
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-white/90 text-[#013523] rounded-full px-2 py-0.5 font-semibold"
        >
          {item.kind === 'tournament' ? 'Tournament'
            : item.kind === 'club_event' ? 'Club Event'
            : item.kind === 'session' ? 'Court Movement'
            : item.kind === 'ladder' ? 'Ladder'
            : 'Round Robin'}
        </span>
        {item.kind === 'club_event' && item.category && CLUB_CATEGORY_LABEL[item.category] && (
          <span
            data-testid={`featured-category-${testIdIndex}`}
            className="text-[10px] uppercase tracking-wider bg-[#f0e6cf] text-[#7c5b1a] border border-[#c9a93a]/40 rounded-full px-2 py-0.5 font-semibold"
          >
            {CLUB_CATEGORY_LABEL[item.category]}
          </span>
        )}
      </div>

      {/* Top-right: viewer state + optional Manage pill for the assigned EM */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        <ManageEventPill eventKind={item.kind} eventId={item.id} variant="card" onClick={(e) => e.stopPropagation()} />
        <ViewerPill state={item.viewer_state} />
      </div>

      {/* Bottom: title + meta. Right padding reserved when a completed CTA is
          shown so the title never sits under the bottom-right results card. */}
      <div className={`absolute inset-x-0 bottom-0 p-4 sm:p-5 text-white ${item.completed_cta ? 'pr-[40%] sm:pr-[34%]' : ''}`}>
        {pill && (
          <div className="flex items-center gap-2 mb-1.5">
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-semibold ${TONE_CLS[pill.tone]}`}
              data-testid={`featured-status-${testIdIndex}`}
            >
              {pill.label}
            </span>
          </div>
        )}
        <div className="text-xl sm:text-2xl font-serif tracking-tight leading-tight drop-shadow line-clamp-2">
          {item.name}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-white/90">
          {item.kind !== 'club_event' && (
            <span className="inline-flex items-center gap-1">
              {(item.event_type || '').replace(/^./, (c) => c.toUpperCase())}
              {COMPOSITION_LABEL[item.composition]
                ? ` · ${COMPOSITION_LABEL[item.composition]}`
                : ''}
            </span>
          )}
          {startsDate && (
            <span className="inline-flex items-center gap-1"><Calendar size={12} /> {startsDate}{startsTime ? ` · ${startsTime}` : ''}</span>
          )}
          {item.location_text && (
            <span className="inline-flex items-center gap-1 truncate max-w-[16em]"><MapPin size={12} /> {item.location_text}</span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-white/85">
          {showRegisteredCount && (
            <span className="inline-flex items-center gap-1"><Users size={12} /> <CountLabel item={item} /></span>
          )}
          {item.kind === 'club_event' && item.rsvp_enabled && (
            <span
              data-testid={`featured-going-${testIdIndex}`}
              className="inline-flex items-center gap-1"
            >
              <Users size={12} /> {item.attendee_count || 0} registered
            </span>
          )}
          {fee && (
            <span data-testid={`featured-fee-${testIdIndex}`}>{fee}</span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function HomeFeaturedHero() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    let alive = true;
    homeFeaturedAPI.list()
      .then((r) => { if (alive) setItems(r.data?.featured || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  const total = items?.length || 0;

  const go = useCallback((newIdx) => {
    if (!total) return;
    setIdx(((newIdx % total) + total) % total);
  }, [total]);

  // Auto-rotate only if >1 slide, not paused, and no reduced-motion.
  useEffect(() => {
    if (!total || total < 2 || paused || reducedMotion) return undefined;
    timerRef.current = window.setInterval(() => {
      setIdx((i) => (i + 1) % total);
    }, AUTOROTATE_MS);
    return () => { if (timerRef.current) window.clearInterval(timerRef.current); };
  }, [total, paused, reducedMotion]);

  const onOpen = (item) => {
    if (item.detail_route) { navigate(item.detail_route); return; }
    if (item.kind === 'tournament') navigate(`/tournaments/${item.id}`);
    else if (item.kind === 'round_robin') navigate(`/round-robins/${item.id}`);
    else if (item.kind === 'club_event')  navigate(`/club-events/${item.id}`);
  };

  // Touch swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0]?.clientX ?? null; setPaused(true); };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) go(idx + 1); else go(idx - 1);
    }
    touchStartX.current = null;
  };

  if (items === null) {
    return (
      <div
        data-testid="home-featured-loading"
        className="rounded-3xl h-40 bg-[#f0ece3]/40 border border-[#e3dcd1] animate-pulse"
        aria-hidden
      />
    );
  }
  if (total === 0) {
    // Hard requirement: NO random fallback. Simply render nothing.
    return <div data-testid="home-featured-empty" style={{ display: 'none' }} aria-hidden />;
  }

  const current = items[idx];

  return (
    <section
      aria-label="Featured events"
      data-testid="home-featured-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="relative"
    >
      <Slide item={current} onOpen={() => onOpen(current)} testIdIndex={idx} />

      {total > 1 && (
        <>
          {/* Prev/Next arrows — minimal, translucent, thin chevron.
              Desktop: low-opacity by default, brighten on hover/focus.
              Mobile: slightly smaller and always visible but subtle. */}
          <button
            type="button"
            aria-label="Previous featured event"
            onClick={(e) => { e.stopPropagation(); setPaused(true); go(idx - 1); }}
            data-testid="featured-prev"
            className="
              group/nav absolute left-2 sm:left-3 top-1/2 -translate-y-1/2
              w-8 h-8 sm:w-8 sm:h-8 md:w-[34px] md:h-[34px]
              rounded-full
              bg-black/25 hover:bg-black/55 focus-visible:bg-black/55
              text-white/85 hover:text-white
              border border-white/20 backdrop-blur-sm
              shadow-[0_1px_2px_rgba(0,0,0,0.15)]
              opacity-80 md:opacity-55 hover:opacity-100 focus-visible:opacity-100
              transition-all duration-150
              flex items-center justify-center
              focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50
            "
          >
            <CaretLeft size={14} weight="regular" className="translate-x-[-0.5px]" />
          </button>
          <button
            type="button"
            aria-label="Next featured event"
            onClick={(e) => { e.stopPropagation(); setPaused(true); go(idx + 1); }}
            data-testid="featured-next"
            className="
              group/nav absolute right-2 sm:right-3 top-1/2 -translate-y-1/2
              w-8 h-8 sm:w-8 sm:h-8 md:w-[34px] md:h-[34px]
              rounded-full
              bg-black/25 hover:bg-black/55 focus-visible:bg-black/55
              text-white/85 hover:text-white
              border border-white/20 backdrop-blur-sm
              shadow-[0_1px_2px_rgba(0,0,0,0.15)]
              opacity-80 md:opacity-55 hover:opacity-100 focus-visible:opacity-100
              transition-all duration-150
              flex items-center justify-center
              focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50
            "
          >
            <CaretRight size={14} weight="regular" className="translate-x-[0.5px]" />
          </button>

          {/* Dots — subtle inactive, elongated pill for active. */}
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/15 backdrop-blur-[2px]">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Show featured event ${i + 1} of ${total}`}
                onClick={(e) => { e.stopPropagation(); setPaused(true); go(i); }}
                data-testid={`featured-dot-${i}`}
                className={`transition-all duration-200 rounded-full ${
                  i === idx
                    ? 'w-5 h-[5px] bg-white/95'
                    : 'w-[5px] h-[5px] bg-white/45 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
